import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, Users, Clock, Filter } from 'lucide-react';
import { LoadingSpinner } from '../ui';
import { api } from '../../services/api';

interface UserMessage {
  id: number;
  content: string;
  sentAt: string;
  type: 'group' | 'direct';
  target: string;
}

interface Props {
  userId: string;
  username: string;
  isOpen: boolean;
  onClose: () => void;
}

export const UserMessageModal: React.FC<Props> = ({ userId, username, isOpen, onClose }) => {
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [filteredMessages, setFilteredMessages] = useState<UserMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [typeFilter, setTypeFilter] = useState<'all' | 'group' | 'direct'>('all');
  const [targetFilter, setTargetFilter] = useState<string>('all');
  const [availableTargets, setAvailableTargets] = useState<string[]>([]);

  const loadUserMessages = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get(`/admin/users/${userId}/messages`);

      if (response.data.success) {
        const msgs = response.data.data.messages;
        setMessages(msgs);

        // Extract unique targets for filter dropdown
        const uniqueTargets = new Set<string>();
        msgs.forEach((m: UserMessage) => uniqueTargets.add(m.target));
        const targets = Array.from(uniqueTargets).sort();
        setAvailableTargets(targets);
      } else {
        throw new Error(response.data.message || 'Failed to load user messages');
      }
    } catch (error) {
      console.error('Failed to load user messages:', error);
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) {
      // Reset filters when modal opens
      setTypeFilter('all');
      setTargetFilter('all');
      loadUserMessages();
    }
  }, [isOpen, userId, loadUserMessages]);

  // Filter messages whenever filters change
  useEffect(() => {
    let filtered = messages;

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(msg => msg.type === typeFilter);
    }

    // Filter by target
    if (targetFilter !== 'all') {
      filtered = filtered.filter(msg => msg.target === targetFilter);
    }

    setFilteredMessages(filtered);
  }, [messages, typeFilter, targetFilter]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return `Vandaag om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInDays === 1) {
      return `Gisteren om ${date.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInDays < 7) {
      return `${diffInDays} dagen geleden`;
    } else {
      return date.toLocaleDateString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-canvas/70 backdrop-blur-sm">
      <div className="bg-surface border border-white/[.08] rounded-card shadow-panel w-full max-w-4xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/[.08] flex justify-between items-center">
          <h3 className="text-lg font-bold text-fg-bright">
            Berichten van {username}
          </h3>
          <button
            onClick={onClose}
            className="text-fg-muted hover:text-fg-bright transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-neon-pink-soft font-semibold mb-2">Fout bij laden</div>
              <p className="text-fg-muted text-sm">{error}</p>
              <button
                onClick={loadUserMessages}
                className="sf-btn-primary mt-4 px-4 py-2 text-sm"
              >
                Opnieuw proberen
              </button>
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="mx-auto h-12 w-12 text-fg-faint mb-4" />
              <h4 className="text-lg font-semibold text-fg-bright mb-2">
                Geen berichten gevonden
              </h4>
              <p className="text-fg-muted">
                Deze gebruiker heeft nog geen berichten verstuurd.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Filters */}
              <div className="sf-card p-4 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Filter className="h-4 w-4 text-neon-cyan" />
                  <h4 className="font-semibold text-fg-bright">Filters</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Type Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                      Type bericht
                    </label>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value as 'all' | 'group' | 'direct')}
                      className="sf-input py-2.5 text-sm"
                    >
                      <option value="all">Alle berichten</option>
                      <option value="group">Groepschat</option>
                      <option value="direct">Direct berichten</option>
                    </select>
                  </div>

                  {/* Target Filter */}
                  <div>
                    <label className="block text-sm font-semibold text-fg-soft mb-1.5">
                      {typeFilter === 'group' ? 'Groep' : typeFilter === 'direct' ? 'Persoon' : 'Ontvanger'}
                    </label>
                    <select
                      value={targetFilter}
                      onChange={(e) => setTargetFilter(e.target.value)}
                      className="sf-input py-2.5 text-sm"
                    >
                      <option value="all">Alle ontvangers</option>
                      {availableTargets
                        .filter(target => {
                          if (typeFilter === 'all') return true;
                          const targetMessages = messages.filter(m => m.target === target);
                          return targetMessages.some(m => m.type === typeFilter);
                        })
                        .map(target => (
                          <option key={target} value={target}>{target}</option>
                        ))
                      }
                    </select>
                  </div>
                </div>

                {/* Filter results info */}
                <div className="mt-3 text-sm text-fg-muted">
                  Toont {filteredMessages.length} van {messages.length} berichten
                  {(typeFilter !== 'all' || targetFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setTypeFilter('all');
                        setTargetFilter('all');
                      }}
                      className="ml-2 sf-link text-sm"
                    >
                      Reset filters
                    </button>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="sf-item p-4 mb-6">
                <h4 className="font-semibold text-fg-bright mb-2">Overzicht</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-fg-bright">
                      {filteredMessages.length}
                    </div>
                    <div className="sf-label-mono mt-1">
                      {typeFilter !== 'all' || targetFilter !== 'all' ? 'Gefilterde berichten' : 'Totaal berichten'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-neon-cyan">
                      {filteredMessages.filter(m => m.type === 'group').length}
                    </div>
                    <div className="sf-label-mono mt-1">Groepschat</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-neon-violet">
                      {filteredMessages.filter(m => m.type === 'direct').length}
                    </div>
                    <div className="sf-label-mono mt-1">Direct berichten</div>
                  </div>
                </div>
              </div>

              {/* Messages List */}
              {filteredMessages.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="mx-auto h-8 w-8 text-fg-faint mb-2" />
                  <p className="text-fg-muted italic">
                    Geen berichten gevonden met de huidige filters
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredMessages.map((message) => (
                  <div key={`${message.type}-${message.id}`} className="sf-item p-4 transition-colors hover:bg-white/[.06]">
                    {/* Message Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        {message.type === 'group' ? (
                          <div className="flex items-center space-x-2">
                            <Users className="h-5 w-5 text-neon-cyan" />
                            <span className="sf-badge-cyan">
                              Groep
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <MessageSquare className="h-5 w-5 text-neon-violet" />
                            <span className="inline-flex items-center rounded-lg bg-neon-violet/[.12] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-neon-violet">
                              Direct
                            </span>
                          </div>
                        )}
                        <span className="font-medium text-fg-bright">
                          {message.target}
                        </span>
                      </div>
                      <div className="flex items-center font-mono text-[11px] text-fg-meta">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatDate(message.sentAt)}
                      </div>
                    </div>

                    {/* Message Content */}
                    <div className="pl-8">
                      <p className="text-fg-body break-words">
                        {message.content || <em className="text-fg-faint">Geen tekst (mogelijk media)</em>}
                      </p>
                    </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/[.08] flex justify-end">
          <button
            onClick={onClose}
            className="sf-btn-secondary px-4 py-2 text-sm"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};
